<template>
  <sp-text-field
    readonly
    :clearable="clearable"
    :persistent-clear="clearable"
    class="activator"
    color="surface"
    :value.prop="value"
    :aria-label="label"
    :placeholder="placeholder"
    part="text-field"
    :input-test-id="inputTestId"
    propagate-append-click
    @click-clear="$emit('clear')"
  >
    <sp-icon
      v-if="prependIcon"
      slot="prepend"
      class="default-filter__activator-icon"
      :name="prependIcon"
      fill-color="var(--sp-comp-filter-default-activator-icon-fill-color)"
    ></sp-icon>
    <sp-icon v-if="appendIcon" slot="append" class="default-filter__append-icon" :name="appendIcon"></sp-icon>
  </sp-text-field>
</template>

<script setup>
import { computed } from "vue";

import { booleanAttribute, toBoolean } from "#ce/utils/props";

defineEmits(["clear"]);

const props = defineProps({
  value: {
    type: String,
    default: undefined,
  },
  label: {
    type: String,
    required: true,
  },
  prependIcon: {
    type: String,
    default: undefined,
  },
  appendIcon: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` attribute on the activator's input. Pass a catalog value from the
   * feature-specific filter (see `app/frontend/src/test-ids.json`).
   */
  inputTestId: {
    type: String,
    default: undefined,
  },
  /**
   * When set, the activator hides its clear button so the value can never be
   * emptied. Used for legally required filters (see `<sp-required-filters-modal>`).
   */
  nonClearable: booleanAttribute(),
});

const clearable = computed(() => !toBoolean(props.nonClearable));

const placeholder = computed(() => props.value ?? props.label);
</script>

<style scoped lang="scss">
@import "@assets/mixins/breakpoint";

.activator {
  --activator-height: 44px;
  --activator-border-color: var(--sp-sys-form-field-border-color);
  --activator-border-width: 1px;
  --activator-border-radius: var(--sp-sys-form-field-border-radius);
  --activator-color: #656f75;

  --sp-comp-text-field-color: var(--activator-color);
  --sp-comp-text-field-padding-inline: var(--sp-ref-scale-8);
  --sp-comp-text-field-placeholder-color: var(--activator-border-color);
  --sp-comp-text-field-background-color: white;
  --sp-comp-text-field-border-color: var(--activator-border-color);
  --sp-comp-text-field-border-width: var(--activator-border-width);
  --sp-comp-text-field-border-radius: var(--activator-border-radius);
  --sp-comp-text-field-min-height: var(--activator-height);
  --sp-comp-text-field-cursor: pointer;
  --sp-comp-default-activator-text-field-padding-inline: var(--sp-ref-scale-10);
  --sp-comp-text-field-content-padding-inline: var(--sp-comp-default-activator-text-field-padding-inline);

  @include sp-breakpoint(sm) {
    --button-justify-content: start;
    --activator-height: var(--sp-comp-default-filter-activator-height, 3.625rem);
    --activator-border-radius: 0;
    --sp-comp-text-field-border-width: var(--activator-border-width);

    margin-bottom: 0;
  }

  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
    box-shadow: none;
  }
}
.default-filter__activator-icon {
  --fill: var(--sp-sys-color-primary);
}
.default-filter__append-icon {
  --fill: var(--sp-sys-form-field-placeholder-color);
}
</style>
